import { DriverAnalysis } from '../types'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts'

export function WeeklyTab({ analysis }: { analysis: DriverAnalysis }) {
  const { weekly_breakdown } = analysis

  const chartData = weekly_breakdown.map(w => ({
    week: w.week,
    driving: w.driving_hours,
    work: +(w.work_minutes / 60).toFixed(1),
  }))

  return (
    <div className="space-y-5">
      {/* Chart */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Weekly Driving Hours</h3>
          <span className="text-[10px] text-red-400">--- 56h weekly limit</span>
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData} barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="week" tick={{ fontSize: 10 }} tickLine={false} />
            <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} domain={[0, 60]} />
            <Tooltip contentStyle={{ fontSize: 11, borderRadius: 4, border: '1px solid #e5e7eb' }} />
            <ReferenceLine y={56} stroke="#ef4444" strokeDasharray="4 3" strokeWidth={1} />
            <Bar dataKey="driving" fill="#1d4ed8" name="Driving (h)" />
            <Bar dataKey="work" fill="#d97706" name="Other Work (h)" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Table */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Weekly Breakdown</h3>
        <div className="border border-gray-200 rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">Week</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500">Driving</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500">Work</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500">Days</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500">Fortnight</th>
                <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500">Weekly</th>
                <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500">Fortnight</th>
              </tr>
            </thead>
            <tbody>
              {weekly_breakdown.map((w) => (
                <tr key={w.week} className={`border-b border-gray-100 ${w.weekly_status === 'infringement' ? 'bg-red-50' : ''}`}>
                  <td className="py-2 px-3 font-medium text-gray-700">{w.week}</td>
                  <td className={`py-2 px-3 text-right font-medium ${w.driving_hours > 56 ? 'text-red-600' : w.driving_hours > 50 ? 'text-amber-600' : 'text-gray-900'}`}>
                    {w.driving_hours}h
                  </td>
                  <td className="py-2 px-3 text-right text-gray-500">{(w.work_minutes / 60).toFixed(1)}h</td>
                  <td className="py-2 px-3 text-right text-gray-500">{w.days_driven}</td>
                  <td className={`py-2 px-3 text-right font-medium ${w.fortnight_driving_hours > 90 ? 'text-red-600' : 'text-gray-500'}`}>
                    {w.fortnight_driving_hours}h
                  </td>
                  <td className="py-2 px-3 text-center">
                    <span className={`inline-block w-2 h-2 rounded-full ${
                      w.weekly_status === 'ok' ? 'bg-green-500' :
                      w.weekly_status === 'warning' ? 'bg-amber-500' : 'bg-red-500'
                    }`} />
                  </td>
                  <td className="py-2 px-3 text-center">
                    <span className={`inline-block w-2 h-2 rounded-full ${
                      w.fortnight_status === 'ok' ? 'bg-green-500' :
                      w.fortnight_status === 'warning' ? 'bg-amber-500' : 'bg-red-500'
                    }`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Regulation note */}
      <div className="border-t border-gray-200 pt-3">
        <p className="text-[11px] text-gray-400">
          <span className="font-semibold text-gray-500">EC 561/2006</span> — Max 56h driving per week (Art. 6(2)). Max 90h driving per rolling fortnight (Art. 6(3)). Daily driving max 9h, extendable to 10h twice per week (Art. 6(1)).
        </p>
      </div>
    </div>
  )
}
